import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { ProductTypes } from '@/types/ecommerce.types';
import { PodFrameBuilderElement } from '@/types/pod-frame-builder';

export type UploadStatus = {
  hasAnyImage: boolean;
  areAllFramesUploaded: boolean;
  uploadedCount: number;
  totalFrames: number;
};

export const useFrameUploadStatus = (): UploadStatus => {
  const { type, galleryTiles } = useSelector((state: RootState) => state.product);
  const isGallery = type === ProductTypes.GALLERY;
  const totalFrames = isGallery && galleryTiles?.tiles ? JSON.parse(galleryTiles.tiles).length : 1;

  const [status, setStatus] = useState<UploadStatus>({
    hasAnyImage: false,
    areAllFramesUploaded: false,
    uploadedCount: 0,
    totalFrames,
  });

  useEffect(() => {
    const podEl = document.querySelector<PodFrameBuilderElement>('pod-frame-builder');
    if (!podEl) return;

    const controller = new AbortController();

    const checkImages = () => {
      const images = podEl.getImages();
      const uploadedCount = images?.length || 0;
      const hasAnyImage = uploadedCount > 0;

      let areAllFramesUploaded = false;
      if (uploadedCount === totalFrames) {
        const positions = new Set(images.map((img) => img.position));
        areAllFramesUploaded = true;
        for (let i = 0; i < totalFrames; i++) {
          if (!positions.has(i)) {
            areAllFramesUploaded = false;
            break;
          }
        }
      }

      setStatus({
        hasAnyImage,
        areAllFramesUploaded,
        uploadedCount,
        totalFrames,
      });
    };

    podEl.addEventListener('load', checkImages, { signal: controller.signal });
    podEl.addEventListener('images-change', checkImages, { signal: controller.signal });

    return () => controller.abort();
  }, [totalFrames]);

  return status;
};

